import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

/**
 * Status configuration for 4-state TISC maturity model
 * Based on 70% threshold for L1/L2 achievement
 */
const STATUS_CONFIG = {
  no_evidence: {
    label: "No Evidence",
    color: "#a04747",
    bgColor: "bg-danger-50",
    textColor: "text-danger-700",
    borderColor: "border-danger-200",
    description: "Requirement not addressed",
  },
  partial_l1: {
    label: "Partial L1",
    color: "#b07a1e",
    bgColor: "bg-warning-50",
    textColor: "text-warning-700",
    borderColor: "border-warning-200",
    description: "Started but below 70% threshold",
  },
  l1_achieved: {
    label: "L1 Achieved",
    color: "#4e7d58",
    bgColor: "bg-success-50",
    textColor: "text-success-700",
    borderColor: "border-success-200",
    description: "Baseline requirements met",
  },
  l2_achieved: {
    label: "L2 Achieved",
    color: "#1f4c54",
    bgColor: "bg-primary-50",
    textColor: "text-primary-600",
    borderColor: "border-primary-200",
    description: "Advanced disclosure demonstrated",
  },
  // Admin only: no checked question failed, but the homepage (Q1) was not checked
  not_fully_assessed: {
    label: "Not fully assessed",
    color: "rgba(27,36,48,0.35)",
    bgColor: "bg-[rgba(31,76,84,0.06)]",
    textColor: "text-[var(--text-soft)]",
    borderColor: "border-[rgba(31,76,84,0.12)]",
    description: "Homepage not checked",
  },
};

/**
 * Admin compliance: "yes", "no" or "not_fully_assessed". Results from before v1.11
 * only have the admin_compliant flag.
 */
const ADMIN_STATUS = {
  yes: { label: "Yes", icon: CheckCircle2, iconClass: "text-success-500", textClass: "text-success-700 font-medium" },
  no: { label: "No", icon: AlertCircle, iconClass: "text-warning-500", textClass: "text-warning-700 font-medium" },
  not_fully_assessed: {
    label: "Not fully assessed (homepage not checked)",
    icon: Info,
    iconClass: "text-[var(--accent)]",
    textClass: "text-[var(--text-primary)] font-medium",
  },
};

/**
 * Shortened requirement names for display
 */
const REQ_SHORT_NAMES = {
  "Administrative Requirements": "Administrative",
  "Structure, Operations & Supply Chains": "Structure & Operations",
  "Policies": "Policies",
  "Risk Assessment": "Risk Assessment",
  "Due Diligence & Remediation": "Due Diligence",
  "Training": "Training",
  "Monitoring & Evaluation": "Monitoring",
  "Outcomes & Effectiveness": "Outcomes",
};

/**
 * Get overall maturity display label
 */
const getOverallMaturityLabel = (maturity) => {
  switch (maturity) {
    case "no_evidence":
      return "No Evidence";
    case "partial_l1":
      return "Partial L1";
    case "l1_achieved":
      return "L1 Achieved";
    case "l2_achieved":
      return "L2 Achieved";
    default:
      return "Unknown";
  }
};

/**
 * Progress bar component for individual requirement
 */
function RequirementProgressBar({ requirement, index }) {
  const config = STATUS_CONFIG[requirement.status] || STATUS_CONFIG.partial_l1;
  // null when the homepage was not checked and nothing else failed: no percentage to show
  const notMeasured = requirement.l1_percentage === null;
  const percentage = requirement.l1_percentage || 0;
  const shortName = REQ_SHORT_NAMES[requirement.name] || requirement.name;
  const homepageUnchecked =
    !notMeasured && Array.isArray(requirement.unassessed_questions) && requirement.unassessed_questions.length > 0;

  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      {/* Requirement header with name, badge, and percentage */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-xs font-medium text-[var(--text-soft)] shrink-0">
            {requirement.id}
          </span>
          <span className="text-sm font-medium text-[var(--text-primary)] truncate">
            {shortName}
          </span>
          {homepageUnchecked && (
            <span className="text-xs text-[var(--text-soft)] shrink-0">(homepage not checked)</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
              config.bgColor,
              config.textColor
            )}
          >
            {config.label}
          </span>
          {notMeasured ? (
            <span className="text-xs text-[var(--text-soft)] text-right">Homepage not checked</span>
          ) : (
            <span className="text-sm font-semibold text-[var(--text-primary)] w-12 text-right">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 w-full rounded-full bg-[rgba(31,76,84,0.08)] overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ backgroundColor: config.color }}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(percentage, 100)}%` }}
          transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.05 }}
        />
        {/* 70% threshold marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-[rgba(27,36,48,0.3)]"
          style={{ left: "70%" }}
          title="70% threshold"
        />
      </div>
    </motion.div>
  );
}

/**
 * Legend component showing all 4 status types
 */
function StatusLegend({ statuses }) {
  return (
    <div className="flex flex-wrap gap-3 pt-2">
      {Object.entries(STATUS_CONFIG)
        .filter(([key]) => key !== "not_fully_assessed" || statuses.has(key))
        .map(([key, config]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: config.color }}
            />
            <span className="text-xs text-[var(--text-soft)]">{config.label}</span>
          </div>
        ))}
    </div>
  );
}

/**
 * TISC Maturity Card - displays Level 1/Level 2 compliance maturity
 * for the 8 TISC requirements using a 4-state model
 *
 * @param {Object} tiscLevels - TISC levels data from backend
 * @param {Object} tiscLevels.summary - Summary statistics
 * @param {Array} tiscLevels.requirements - Array of 8 requirement objects
 * @param {Object} externalChecks - Optional external evidence (homepage/registry) from analysis_metadata
 */
export default function TISCMaturityCard({ tiscLevels, externalChecks }) {
  if (!tiscLevels || !tiscLevels.requirements) {
    return null;
  }

  const { summary, requirements } = tiscLevels;
  const overallConfig = STATUS_CONFIG[summary.overall_maturity] || STATUS_CONFIG.partial_l1;
  const homepageInfo = externalChecks?.homepage;
  const homepageSkipped =
    homepageInfo?.status === "unknown" && homepageInfo?.reason === "no_homepage_url";
  const homepageFetchFailed =
    homepageInfo?.status === "unknown" && homepageInfo?.reason === "fetch_failed";
  const homepageAssumedPass = homepageInfo?.assumed_pass;
  // The three flags above only match results from before v1.11; newer results show the
  // homepage check in HomepageCheckNote on the results page.
  const adminStatus = ADMIN_STATUS[summary.admin_status] || (summary.admin_compliant ? ADMIN_STATUS.yes : ADMIN_STATUS.no);
  const AdminIcon = adminStatus.icon;
  const usedStatuses = new Set(requirements.map((req) => req.status));

  return (
    <section className="space-y-6 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-8 shadow-[0_28px_56px_rgba(15,23,42,0.08)]">
      {/* Header */}
      <header className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="eyebrow-muted">
              TISC compliance maturity
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              International Reporting Template (Level 1 &amp; Level 2)
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Overall maturity badge */}
            <span
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider",
                overallConfig.bgColor,
                overallConfig.textColor
              )}
            >
              Overall: {getOverallMaturityLabel(summary.overall_maturity)}
            </span>
          </div>
        </div>

        {/* Summary stats */}
        <div className="flex flex-wrap gap-4 rounded-xl bg-[rgba(31,76,84,0.03)] p-4">
          <div className="flex items-center gap-2">
            <AdminIcon className={cn("h-4 w-4", adminStatus.iconClass)} />
            <span className="text-sm text-[var(--text-secondary)]">
              Admin Compliant:{" "}
              <span className={adminStatus.textClass}>{adminStatus.label}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-[var(--accent)]" />
            <span className="text-sm text-[var(--text-secondary)]">
              L1+ Achieved:{" "}
              <span className="font-medium text-[var(--text-primary)]">
                {summary.l1_achieved_count + summary.l2_achieved_count}/8
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-primary-500" />
            <span className="text-sm text-[var(--text-secondary)]">
              L2 Achieved:{" "}
              <span className="font-medium text-[var(--text-primary)]">
                {summary.l2_achieved_count}/7
              </span>
            </span>
          </div>
          {(homepageSkipped || homepageFetchFailed || homepageAssumedPass) && (
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-[var(--accent)]" />
              <span className="text-sm text-[var(--text-secondary)]">
                {homepageSkipped && "Homepage not provided — link check skipped (Admin assumed OK)."}
                {homepageFetchFailed && "Homepage check failed to fetch — not penalised; Admin assumed OK."}
                {!homepageSkipped && !homepageFetchFailed && homepageAssumedPass && "Homepage check skipped (Admin assumed OK)."}
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Requirements list */}
      <div className="space-y-4">
        {requirements.map((req, index) => (
          <RequirementProgressBar
            key={req.id}
            requirement={req}
            index={index}
          />
        ))}
      </div>

      {/* Legend and threshold note */}
      <footer className="space-y-3 border-t border-[rgba(31,76,84,0.08)] pt-4">
        <StatusLegend statuses={usedStatuses} />
        <p className="text-xs text-[var(--text-soft)] italic">
          Threshold: 70% required to achieve L1 or L2. Admin requirements use strict mode (all must be met).
        </p>
      </footer>
    </section>
  );
}
